/**
 * Every endpoint in the prism-new API doc
 * (https://testprism.bridgingtech.com/prism-new/docs), as `METHOD /path` under `/api/v1`.
 *
 * ApiService sends a request to `environment.prismNew.apiUrl` when its method + path matches a
 * line here; anything not listed stays on the old API (`environment.apiUrl`). `{name}` matches one
 * path segment, `{*}` matches the rest of the path.
 *
 * To move one endpoint back to the old API, delete its line. To move all of them back, set
 * `prismNew.enabled` to false in the environment file.
 */
export const PRISM_NEW_ENDPOINTS: readonly string[] = [
  // access
  'GET /access/{*}',
  'PUT /access/{*}',
  // auth
  'GET /auth/whoami',
  'POST /auth/revoke',
  'GET /auth/dev/api-keys',
  'POST /auth/dev/api-keys/mint',
  'POST /auth/dev/reset-throttle',
  'POST /auth/validate-email',
  'POST /auth/verify-otp',
  'POST /auth/resend-otp',
  'POST /auth/setup-password',
  'POST /auth/login',
  'POST /auth/login/2fa',
  'POST /auth/google',
  'POST /auth/refresh',
  'GET /auth/authenticate',
  'GET /auth/get-setting',
  'POST /auth/logout',
  'POST /auth/change-password',
  'POST /auth/forgot-password',
  'POST /auth/reset-password',
  'POST /auth/set-password',
  'POST /auth/access-request',
  'POST /auth/access-request/products',
  'GET /auth/access-request/list',
  'PATCH /auth/access-request/{id}/status',
  // product-dashboard
  'GET /product-dashboard/{product}/session',
  // app-routes
  'POST /app-routes/create-route',
  'GET /app-routes/list-routes',
  'GET /app-routes/get-route/{id}',
  'PUT /app-routes/update-route',
  'POST /app-routes/add-role-route-access',
  'PUT /app-routes/update-status',
  'DELETE /app-routes/delete-route/{id}',
  'GET /app-routes/get-product-routes/{id}',
  'GET /app-routes/list-role-route-access',
  'GET /app-routes/list-app-route-module',
  // dev/email-templates
  'GET /dev/email-templates',
  'GET /dev/email-templates/api',
  'GET /dev/email-templates/api/{name}',
  'GET /dev/email-templates/{name}/preview',
  // audit-logs
  'GET /audit-logs',
  'POST /audit-logs',
  'GET /audit-logs/{id}',
  // catalog
  'GET /catalog/products',
  'GET /catalog/products/{productPid}/pages',
  'POST /catalog/products/{productPid}/pages',
  'GET /catalog/pages/{pageId}/reset-snapshots/latest',
  'POST /catalog/pages/{pageId}/reset-snapshots',
  'GET /catalog/pages/{pageId}/widgets',
  'POST /catalog/pages/{pageId}/widgets',
  'POST /catalog/pages/{pageId}/widgets/batch',
  'PATCH /catalog/widgets/{widgetId}/platforms/{platform}',
  // agent-records
  'POST /agent-records/create',
  'GET /agent-records/all',
  'GET /agent-records/{id}',
  'DELETE /agent-records/{id}',
  // call-audit
  'POST /call-audit/report-submit',
  'POST /call-audit/report',
  'GET /call-audit/reports',
  'GET /call-audit/report-by-conversation-pid',
  'GET /call-audit/filter-options',
  'POST /call-audit/media-ticket',
  'GET /call-audit/stream-audio/{botId}',
  'GET /call-audit/audio-duration/{botId}',
  'POST /call-audit/share-link',
  'GET /call-audit/shared',
  'GET /call-audit/shared/stream-audio/{botId}',
  'GET /call-audit/shared/audio-duration/{botId}',
  'PATCH /call-audit/{id}/verify',
  'POST /call-audit/events',
  'GET /call-audit/session/access',
  'POST /call-audit/session/start',
  'POST /call-audit/session/close',
  'GET /call-audit/{id}/session/active',
  'POST /call-audit/review-time',
  'GET /call-audit/{id}/activity',
  'POST /call-audit/feedback',
  'GET /call-audit/{id}/feedback',
  // campaign-metrics
  'GET /campaign-metrics',
  'POST /campaign-metrics',
  'GET /campaign-metrics/overall-performance',
  'GET /campaign-metrics/by-date',
  'GET /campaign-metrics/latest',
  // call-history
  'GET /call-history/list',
  'GET /call-history/grouped',
  'GET /call-history/history',
  'GET /call-history/details',
  // customer-records
  'POST /customer-records',
  'GET /customer-records/all',
  'GET /customer-records/{id}',
  'PATCH /customer-records/{id}',
  'DELETE /customer-records/{id}',
  // live-sale-pitch
  'POST /live-sale-pitch/conversations',
  // countries
  'GET /countries',
  'POST /countries',
  // dashboards
  'GET /dashboards',
  'POST /dashboards',
  'POST /dashboards/{dashboardId}/widgets',
  'PUT /dashboards/{dashboardId}/widgets',
  'POST /dashboards/{dashboardId}/widgets/batch',
  'DELETE /dashboards/{dashboardId}/widgets/{widgetId}',
  'PATCH /dashboards/{dashboardId}/widgets/{widgetId}/visibility',
  'POST /dashboards/{dashboardId}/reset',
  'PATCH /dashboards/{dashboardId}/widgets/{placementId}/size',
  'PATCH /dashboards/{dashboardId}/widgets/{placementId}/custom-config',
  // deck-admin
  'GET /deck-admin/industries',
  'GET /deck-admin/{product}/groups',
  'POST /deck-admin/{product}/groups',
  'GET /deck-admin/{product}/groups/{groupKey}',
  'PATCH /deck-admin/{product}/groups/{groupKey}',
  'DELETE /deck-admin/{product}/groups/{groupKey}',
  'GET /deck-admin/{product}/groups/{groupKey}/slides',
  'POST /deck-admin/{product}/groups/{groupKey}/slides',
  'GET /deck-admin/{product}/groups/{groupKey}/slides/{pageKey}',
  'PATCH /deck-admin/{product}/groups/{groupKey}/slides/{pageKey}',
  'DELETE /deck-admin/{product}/groups/{groupKey}/slides/{pageKey}',
  // extensions
  'GET /extensions',
  'POST /extensions/{app}/register',
  'GET /extensions/{app}/update.xml',
  'GET /extensions/{app}/latest',
  'GET /extensions/{app}/deepgram-key',
  'POST /extensions/{app}/publish',
  // partner
  'POST /partner/tokens/refresh',
  'GET /partner/tokens',
  // platform/dbinfo
  'GET /platform/dbinfo/connections',
  'GET /platform/dbinfo/tables',
  // product
  'GET /product/list-product',
  'GET /product/list-teams/{productId}',
  'POST /product/create-team',
  'PUT /product/update-team',
  'DELETE /product/delete-team/{teamId}',
  // prompts
  'POST /prompts/create-ai-task',
  'GET /prompts/list-ai-tasks',
  'GET /prompts/get-ai-task/{id}',
  'POST /prompts/create-master-prompt',
  'GET /prompts/list-master-prompts',
  'GET /prompts/get-master-prompt',
  'POST /prompts/update-master-prompt',
  'GET /prompts/list-prompt-categories',
  'GET /prompts/get-prompt-category/{id}',
  'GET /prompts/get-prompt/{id}',
  'GET /prompts/list-prompts',
  'POST /prompts/create-call-type',
  'GET /prompts/list-call-types',
  // google-meet-inhouse
  'GET /google-meet-inhouse/call-logs',
  'POST /google-meet-inhouse/call-logs/{conversationPid}/summary',
  'POST /google-meet-inhouse/start-bot',
  'POST /google-meet-inhouse/start-bot-v1',
  'POST /google-meet-inhouse/stop-bot',
  'GET /google-meet-inhouse/active-bots',
  'GET /google-meet-inhouse/bot/{botId}/status',
  'GET /google-meet-inhouse/bot/{botId}/detailed-status',
  'GET /google-meet-inhouse/bot/{botId}/info',
  'POST /google-meet-inhouse/bot/{botId}/agent-name',
  // recall-bot
  'POST /recall-bot/webhook',
  'POST /recall-bot/join',
  'POST /recall-bot/leave',
  // key-manager
  'POST /key-manager/create',
  'GET /key-manager/list',
  'POST /key-manager/access/bulk',
  'GET /key-manager/pid/{pid}',
  'PUT /key-manager/pid/{pid}',
  'POST /key-manager/pid/{pid}/revoke',
  'POST /key-manager/pid/{pid}/rotate',
  'GET /key-manager/pid/{pid}/original-key',
  'GET /key-manager/open/{id}',
  'POST /key-manager/pid/{pid}/regenerate-access-token',
  'GET /key-manager/pid/{pid}/access-url',
  'GET /key-manager/{id}',
  // auth/api-keys (machine keys for extension, socket and module clients)
  'GET /auth/api-keys',
  'POST /auth/api-keys',
  'GET /auth/api-keys/scopes',
  'GET /auth/api-keys/{id}/reveal',
  'POST /auth/api-keys/{id}/revoke',
  'POST /auth/api-keys/{id}/rotate',
  // lead-overview
  'POST /lead-overview/summary',
  // live-call-agent
  'GET /live-call-agent/models',
  'POST /live-call-agent/models',
  'PATCH /live-call-agent/models/{modelId}',
  'DELETE /live-call-agent/models/{modelId}',
  'GET /live-call-agent/auth',
  'POST /live-call-agent/auth',
  'PATCH /live-call-agent/auth/{authId}',
  'DELETE /live-call-agent/auth/{authId}',
  'GET /live-call-agent/{product}/tools',
  'POST /live-call-agent/{product}/tools',
  'PATCH /live-call-agent/{product}/tools/{toolId}',
  'DELETE /live-call-agent/{product}/tools/{toolId}',
  'GET /live-call-agent/{product}/prompts',
  'POST /live-call-agent/{product}/prompts',
  'GET /live-call-agent/{product}/prompts/{promptId}',
  'PATCH /live-call-agent/{product}/prompts/{promptId}',
  'DELETE /live-call-agent/{product}/prompts/{promptId}',
  'GET /live-call-agent/{product}/prompts/{promptId}/versions',
  'POST /live-call-agent/{product}/prompts/{promptId}/versions',
  'GET /live-call-agent/{product}/prompts/{promptId}/versions/{version}',
  'POST /live-call-agent/{product}/prompts/{promptId}/versions/{version}/restore',
  'POST /live-call-agent/requests',
  'GET /live-call-agent/requests/{requestId}',
  'DELETE /live-call-agent/requests/{requestId}',
  'POST /live-call-agent/availability',
  'GET /live-call-agent/inbox',
  'POST /live-call-agent/requests/{requestId}/accept',
  'POST /live-call-agent/requests/{requestId}/reject',
  'GET /live-call-agent/presence/me',
  'PATCH /live-call-agent/presence/me',
  // releases
  'GET /releases',
  'POST /releases',
  'GET /releases/{id}',
  'PATCH /releases/{id}',
  'DELETE /releases/{id}',
  // seo
  'GET /seo/metrics',
  'POST /seo/metrics',
  'GET /seo/overall-seo-performance/{id}',
  'GET /seo/click-performance-details',
  'GET /seo/ratings',
  'POST /seo/ratings',
  'GET /seo/city-users',
  'POST /seo/city-users',
  'GET /seo/country-stats',
  'POST /seo/country-stats',
  'GET /seo/top-records',
  'GET /seo/rating-platforms',
  'POST /seo/create',
  'GET /seo/reviews',
  'POST /seo/reviews',
  'PATCH /seo/reviews/{id}',
  'GET /seo/keyword-rankings',
  'GET /seo/keywords/{productId}',
  'POST /seo/keyword-ranking',
  'GET /seo/ai-overview-keywords',
  'POST /seo/ai-overview-keywords',
  'GET /seo/ai-keywords/{productId}',
  'DELETE /seo/metrics/{id}',
  'DELETE /seo/ratings/{id}',
  'DELETE /seo/city-users/{id}',
  'DELETE /seo/country-stats/{id}',
  'DELETE /seo/ai-overview-keywords/{id}',
  'DELETE /seo/reviews/{id}',
  'DELETE /seo/keyword-ranking/{id}',
  // session-status
  'GET /session-status',
  // social-platform
  'GET /social-platform/list-social-platform',
  'POST /social-platform/create-stat',
  'GET /social-platform/get-stat/{id}',
  'PUT /social-platform/update-stat',
  'DELETE /social-platform/delete-stat/{id}',
  'GET /social-platform/list-stats',
  'GET /social-platform/overall-performance-details/{id}',
  'GET /social-platform/platform-performance-details',
  'GET /social-platform/list-social-stats',
  'GET /social-platform/overall-analytics/{id}',
  // admin/system-settings
  'POST /admin/system-settings/regenerate-global-password',
  // summary
  'GET /summary/headlines/latest',
  'GET /summary/cron-configs',
  'POST /summary/cron-configs',
  'POST /summary/cron-configs/execute',
  'GET /summary/cron-configs/{pid}',
  'PATCH /summary/cron-configs/{pid}',
  'POST /summary/query',
  'GET /summary/history/{sessionId}',
  'GET /summary/sessions',
  'PUT /summary/sessions/{sessionId}',
  'GET /summary/stats',
  // storage
  'GET /storage/download',
  'GET /storage/object/{*}',
  'POST /storage/finalize-recording',
  'POST /storage/upload/public',
  // roles
  'POST /roles/create',
  'GET /roles/list',
  'GET /roles/stats/overview',
  'POST /roles/product-access',
  'PUT /roles/update',
  'GET /roles/{id}',
  'PUT /roles/{id}',
  'DELETE /roles/{id}',
  'GET /roles/{id}/users',
  'POST /roles/{id}/restore',
  'POST /roles/{id}/clone',
  // transcription
  'POST /transcription/full',
  // user
  'PATCH /user/profile',
  'GET /user',
  'POST /user',
  'GET /user/list-by-product',
  'POST /user/product-access',
  'GET /user/{id}',
  'PUT /user/{id}',
  'DELETE /user/{id}',
  'POST /user/{id}/restore',
  // vocal-trans-eng
  'POST /vocal-trans-eng/agent-init',
  'GET /vocal-trans-eng/call-lifecycle/{conversationPid}',
  'POST /vocal-trans-eng/customer-sync',
  'POST /vocal-trans-eng/sale-pitch',
  'POST /vocal-trans-eng/call-stream-start',
  'POST /vocal-trans-eng/transcript-summary',
  'POST /vocal-trans-eng/summary-transcript-logs',
  'GET /vocal-trans-eng/all-leads-details',
  'GET /vocal-trans-eng/sale-pitch-logs',
  'GET /vocal-trans-eng/ongoing-calls',
  'GET /vocal-trans-eng/ongoing-calls/{conversationPid}/{traceId}',
  // rag
  'GET /rag/collections',
  'POST /rag/collections',
  'GET /rag/collections/{id}',
  'DELETE /rag/collections/{id}',
  'PUT /rag/collections/{id}/for-env',
  'PUT /rag/collections/{id}/toggle-collection',
  'POST /rag/documents/upload',
  'GET /rag/documents',
  'GET /rag/documents/{id}',
  'DELETE /rag/documents/{id}',
  'POST /rag/documents/{id}/restore',
  'GET /rag/documents/{id}/presigned-url',
  'DELETE /rag/documents/{id}/vector-db',
  'POST /rag/documents/reconcile-archives',
  'POST /rag/query',
  'GET /rag/scrape',
  'POST /rag/scrape',
  'GET /rag/scrape/{id}',
  'GET /rag/features',
  'POST /rag/features',
  'GET /rag/features/{id}',
  'PUT /rag/features/{id}',
  'DELETE /rag/features/{id}',
  'GET /rag/feature-options',
  'POST /rag/feature-options',
  'PUT /rag/feature-options/{id}',
  'DELETE /rag/feature-options/{id}',
  'GET /rag/products',
  'GET /rag/deletion-requests',
  'POST /rag/deletion-requests',
  'GET /rag/deletion-requests/{id}',
  'DELETE /rag/deletion-requests/{id}',
  'GET /rag/vector-db/collections',
  'GET /rag/vector-db/collections/{name}',
  'POST /rag/vector-db/collections/{name}/points/count',
  'POST /rag/vector-db/collections/{name}/points/scroll',
  'POST /rag/vector-db/collections/{name}/points/retrieve',
  'POST /rag/vector-db/collections/{name}/points/search',
  // bot-builder: listed for the API doc. MicroServiceHttpService sends every whitelabel call to
  // prism-new while `prismNew.enabled` is on, so deleting a line here does not move it back.
  'POST /bot-builder/chat',
  'POST /bot-builder/chat/upload-document',
  'GET /bot-builder/chat/history',
  'GET /bot-builder/chat/sessions',
  'GET /bot-builder/chat/sessions/messages',
  'GET /bot-builder/chat/sources',
  'GET /bot-builder/chatbot/bot',
  'POST /bot-builder/chatbot/bot',
  'GET /bot-builder/chatbot/bot/{id}',
  'DELETE /bot-builder/chatbot/bot/{id}',
  'GET /bot-builder/chatbot/bot/{id}/settings',
  'PATCH /bot-builder/chatbot/bot/{id}/settings',
  'GET /bot-builder/chatbot/bot/{chatbotId}/security/ai-providers',
  'PUT /bot-builder/chatbot/bot/{chatbotId}/security/ai-provider-api-key',
  'GET /bot-builder/chatbot/bot/{chatbotId}/security/auth-token',
  'PUT /bot-builder/chatbot/bot/{chatbotId}/security/auth-token',
  'GET /bot-builder/chatbot/bot/{chatbotId}/security/user-list',
  'POST /bot-builder/chatbot/bot/{chatbotId}/security/user-list',
  'POST /bot-builder/chatbot/bot/{chatbotId}/security/auth-token/rotate',
  'GET /bot-builder/chatbot/bot/{chatbotId}/security/auth-token/verify',
  'GET /bot-builder/chatbot/bot/{chatbotId}/workflow',
  'PUT /bot-builder/chatbot/bot/{chatbotId}/workflow',
  'GET /bot-builder/chatbot/bot/{chatbotId}/workflow-node/{nodePid}',
  'PATCH /bot-builder/chatbot/schedules/{scheduleId}/toggle',
  'GET /bot-builder/chatbot/schedules/{scheduleId}/run-logs',
  'GET /bot-builder/chatbot/bot/{id}/schedule-run-logs',
  'POST /bot-builder/agent-system-prompt/generate',
  'GET /bot-builder/agent-system-prompt/product-description',
  'POST /bot-builder/agent-system-prompt/product-description',
  'DELETE /bot-builder/agent-system-prompt/product-description/{productId}',
  'GET /bot-builder/default-config/llm-providers',
  'POST /bot-builder/default-config/llm-providers',
  'DELETE /bot-builder/default-config/llm-providers/{id}',
  'GET /bot-builder/default-config/llm-models/{id}',
  'DELETE /bot-builder/default-config/llm-models/{id}',
  'POST /bot-builder/default-config/llm-models',
  'GET /bot-builder/default-config/default-prompts',
  'POST /bot-builder/default-config/default-prompts',
  'GET /bot-builder/default-config/default-prompts/label/{label}',
  'GET /bot-builder/default-config/default-prompts/{id}',
  'PUT /bot-builder/default-config/default-prompts/{id}',
  'DELETE /bot-builder/default-config/default-prompts/{id}',
  'PUT /bot-builder/default-config/default-prompts/{id}/label',
  'POST /bot-builder/default-config/default-prompts/{id}/restore',
  'GET /bot-builder/assistant-iframe-config',
  'POST /bot-builder/assistant-iframe-config',
  'GET /bot-builder/assistant-iframe-config/{pid}',
  'PATCH /bot-builder/assistant-iframe-config/{pid}',
  'DELETE /bot-builder/assistant-iframe-config/{pid}',
  'GET /bot-builder/assistant-iframe-config/chatbot/{chatbot_id}',
  'POST /bot-builder/knowledge-base/collection',
  'GET /bot-builder/knowledge-base/documents',
  'POST /bot-builder/knowledge-base/documents',
  'POST /bot-builder/knowledge-base/collection/link',
  'GET /bot-builder/knowledge-base/collection/{collectionId}/documents',
  'DELETE /bot-builder/knowledge-base/document/{itemId}',
  'GET /bot-builder/chatbots/{chatbotId}/schedules',
  'POST /bot-builder/chatbots/{chatbotId}/schedules',
  'GET /bot-builder/chatbots/{chatbotId}/schedules/dag-executions/{executionId}',
  'PUT /bot-builder/chatbots/{chatbotId}/schedules/{pid}',
  'DELETE /bot-builder/chatbots/{chatbotId}/schedules/{pid}',
  'POST /bot-builder/chatbots/{chatbotId}/schedules/{pid}/trigger',
  'POST /bot-builder/chatbots/{chatbotId}/schedules/{pid}/backfill',
  'GET /bot-builder/chatbots/{chatbotId}/schedules/{pid}/runs',
  'GET /bot-builder/chatbots/{chatbotId}/delivery-config',
  'POST /bot-builder/chatbots/{chatbotId}/delivery-config',
  'DELETE /bot-builder/chatbots/{chatbotId}/delivery-config',
  'POST /bot-builder/chatbots/{chatbotId}/delivery-config/test',
  'POST /bot-builder/tools',
  'GET /bot-builder/tools/chatbot/{chatbotId}',
  'GET /bot-builder/tools/node/{nodeId}',
  'GET /bot-builder/tools/{toolId}',
  'PATCH /bot-builder/tools/{toolId}',
  'DELETE /bot-builder/tools/{toolId}',
  'POST /bot-builder/tools/{toolId}/link/node/{nodeId}',
  'DELETE /bot-builder/tools/{toolId}/link/node/{nodeId}',
  'GET /bot-builder/traces',
  'GET /bot-builder/traces/sessions',
  'GET /bot-builder/traces/session',
  'GET /bot-builder/traces/detail',
  'GET /bot-builder/users',
  'GET /bot-builder/health',
  'GET /bot-builder/health/db',
  // lead-agents (Lead Finder calling agents)
  'GET /lead-agents',
  'POST /lead-agents',
  'GET /lead-agents/{*}',
  'POST /lead-agents/{*}',
  'PATCH /lead-agents/{id}',
];
